import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const SessionTimeoutModal = () => {
    const [visible, setVisible] = useState(false);
    const [countdown, setCountdown] = useState(60);
    const intervalRef = useRef(null);
    const { user, logout } = useAuth();

    const handleLogoutNow = useCallback(async () => {
        setVisible(false);
        clearInterval(intervalRef.current);
        await logout();
        window.location.href = '/login?reason=timeout';
    }, [logout]);

    const handleStayLoggedIn = useCallback(async () => {
        setVisible(false);
        setCountdown(60);
        clearInterval(intervalRef.current);
        try {
            await api.post('/auth/ping');
        } catch (err) {
            console.warn('[SessionTimeout] Ping failed:', err);
        }
        // Tell AuthContext to reset its timers
        window.dispatchEvent(new CustomEvent('session-keep-alive'));
    }, []);

    useEffect(() => {
        if (!user) return;

        const handleWarning = () => {
            setVisible(true);
            setCountdown(60);

            clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(intervalRef.current);
                        // Time's up — force logout
                        handleLogoutNow();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        };

        const handleActivity = () => {
            // User became active again (mouse/key) — dismiss if showing
            if (visible) {
                setVisible(false);
                setCountdown(60);
                clearInterval(intervalRef.current);
            }
        };

        window.addEventListener('session-warning', handleWarning);
        window.addEventListener('session-activity', handleActivity);

        return () => {
            window.removeEventListener('session-warning', handleWarning);
            window.removeEventListener('session-activity', handleActivity);
            clearInterval(intervalRef.current);
        };
    }, [user, visible, handleLogoutNow]);

    if (!visible) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                {/* Header */}
                <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-amber-900">Session Expiring</h3>
                        <p className="text-sm text-amber-700">Your session will expire due to inactivity</p>
                    </div>
                </div>

                {/* Body */}
                <div className="px-6 py-6 text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-50 border-4 border-red-100 mb-4">
                        <span className="text-3xl font-bold text-red-600 tabular-nums">{countdown}</span>
                    </div>
                    <p className="text-gray-700 text-sm">
                        You will be automatically logged out in <strong>{countdown} second{countdown !== 1 ? 's' : ''}</strong>.
                    </p>
                    <p className="text-gray-500 text-xs mt-2">
                        Click "Stay Logged In" to continue your session.
                    </p>
                </div>

                {/* Actions */}
                <div className="px-6 pb-6 flex gap-3">
                    <button
                        onClick={handleLogoutNow}
                        className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                        Log Out Now
                    </button>
                    <button
                        onClick={handleStayLoggedIn}
                        className="flex-1 px-4 py-2.5 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Stay Logged In
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SessionTimeoutModal;
